'use memo'

import type {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
} from 'ag-grid-community'
import type {
  OrgRegistrationDivision,
  OrgRegistrationKind,
} from '@sportninja/common/features/organization/registration/types'
import { getFilledPercent } from '@sportninja/common/features/organization/registration/utils/availability'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import FeeCell from '../components/cells/FeeCell'
import SpotsCell from '../components/cells/SpotsCell'

type Params = ICellRendererParams<OrgRegistrationDivision>
type Column =
  | ColDef<OrgRegistrationDivision>
  | ColGroupDef<OrgRegistrationDivision>

const KIND_HEADERS: Record<OrgRegistrationKind, string> = {
  player: 'Player Registration',
  team: 'Team Registration',
}

/**
 * Fee and Spots for one kind of registration, under a spanning header. A
 * division that doesn't take that kind shows a dash in both.
 */
const kindGroup = (
  kind: OrgRegistrationKind,
  inactiveAvailabilityLabel: string
): ColGroupDef<OrgRegistrationDivision> => ({
  headerName: capitalizeWords(KIND_HEADERS[kind]),
  children: [
    {
      colId: `${kind}Fee`,
      headerName: capitalizeWords('Fee'),
      minWidth: 130,
      flex: 1,
      valueGetter: ({ data }) => data?.[kind]?.fee ?? null,
      cellRenderer: ({ data }: Params) =>
        data ? <FeeCell option={data[kind]} currency={data.currency} /> : null,
    },
    {
      colId: `${kind}Spots`,
      headerName: capitalizeWords('Spots'),
      minWidth: 190,
      flex: 1.4,
      sortable: !inactiveAvailabilityLabel,
      valueGetter: ({ data }) => {
        const option = data?.[kind]
        return option
          ? getFilledPercent(option.capacity, option.spotsLeft)
          : null
      },
      cellRenderer: ({ data }: Params) =>
        data ? (
          <SpotsCell
            option={data[kind]}
            inactiveLabel={inactiveAvailabilityLabel || undefined}
          />
        ) : null,
    },
  ],
})

/**
 * The divisions table's columns: the division itself, then player and team
 * registration side by side — each with its own fee and spots, since a
 * division can take either or both. Every column has a minimum width that
 * fits its content, so a narrow card scrolls the table sideways instead of
 * clipping a cell.
 *
 * @param inactiveAvailabilityLabel — shown in place of live spots while the
 * registration isn't open ("Opens Jan 15", "Closed"); '' while open.
 */
export const useDivisionColumns = (
  inactiveAvailabilityLabel: string
): Column[] => {
  'use memo'

  return [
    {
      field: 'name',
      headerName: capitalizeWords('Division'),
      minWidth: 170,
      flex: 1.6,
    },
    {
      field: 'ageLabel',
      headerName: capitalizeWords('Age'),
      minWidth: 90,
      flex: 0.7,
    },
    {
      field: 'genderLabel',
      headerName: capitalizeWords('Gender'),
      minWidth: 110,
      flex: 0.8,
    },
    kindGroup('player', inactiveAvailabilityLabel),
    kindGroup('team', inactiveAvailabilityLabel),
  ]
}
